import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Nav, Tab, Spinner, Alert } from 'react-bootstrap';
import api from '../api/axios';

const CaseStudyReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { results } = useSelector((state) => state.exam);
  const startIndex = location.state?.startIndex || 0;
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [caseStudy, setCaseStudy] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/case-study.php?id=${id}`);
        setCaseStudy(res.data.caseStudy);
        setQuestions(res.data.questions);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (!results || !Array.isArray(results) || results.length === 0) {
    return (
      <Container className="mt-5 text-center">
        <p>No results to review. Please complete a case study first.</p>
        <Button variant="outline-primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (loading) return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;

  const currentResult = results[currentIndex];
  // Use index to get the corresponding full question (order should match)
  const fullQuestion = questions[currentIndex] || {};

  const current = {
    ...fullQuestion,
    selected: currentResult.selected,
    score: currentResult.score,
  };

  const total = results.length;

  const handleNext = () => {
    if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1);
  };
  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const renderOptions = () => {
    if (current.mode === 'MATRIX') {
      const columns = current.matrix_columns ? current.matrix_columns.split(',') : [];
      const rows = current.matrix_rows ? current.matrix_rows.split(';') : [];
      const correctArray = current.correct_answer ? current.correct_answer.split(',') : [];
      const selectedArray = current.selected ? current.selected.split(',') : [];

      if (!columns.length || !rows.length) {
        return <Alert variant="warning">Matrix data not available.</Alert>;
      }

      return (
        <table className="table table-bordered">
          <thead>
            <tr>
              <th></th>
              {columns.map((col, colIdx) => <th key={colIdx}>{col}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td>{row}</td>
                {columns.map((col, colIdx) => {
                  const isSelected = selectedArray[rowIdx] === col;
                  const isCorrect = correctArray[rowIdx] === col;
                  let symbol = '';
                  let symbolColor = '';
                  if (isCorrect) {
                    symbol = '✓';
                    symbolColor = 'green';
                  } else if (isSelected && !isCorrect) {
                    symbol = '✗';
                    symbolColor = 'red';
                  }
                  return (
                    <td key={colIdx} style={{ textAlign: 'center' }}>
                      <span style={{ color: symbolColor, fontWeight: 'bold', marginRight: 4 }}>
                        {symbol}
                      </span>
                      <input
                        type="radio"
                        checked={isSelected}
                        disabled
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      // MC or SATA
      const options = ['A', 'B', 'C', 'D', 'E', 'F'].filter(opt => current[`option_${opt.toLowerCase()}`]);
      const selectedAnswers = current.selected ? current.selected.split(',') : [];
      const correctAnswers = current.correct_answer ? current.correct_answer.split(',') : [];
      const isMC = current.mode === 'MC';

      return (
        <div>
          {options.map(opt => {
            const optText = current[`option_${opt.toLowerCase()}`];
            const isSelected = selectedAnswers.includes(opt);
            const isCorrect = correctAnswers.includes(opt);
            let symbol = '';
            let symbolColor = 'transparent';
            let textColor = 'inherit';
            if (isCorrect) {
              symbol = '✓';
              symbolColor = 'green';
              textColor = 'green';
            } else if (isSelected && !isCorrect) {
              symbol = '✗';
              symbolColor = 'red';
              textColor = 'red';
            }
            return (
              <div key={opt} style={{ margin: '8px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                  <span style={{ width: 20, color: symbolColor, fontWeight: 'bold', marginRight: 8 }}>
                    {symbol}
                  </span>
                  <input
                    type={isMC ? 'radio' : 'checkbox'}
                    checked={isSelected}
                    disabled
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ color: textColor }}>
                    {opt}. {optText}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      );
    }
  };

  return (
    <Container fluid className="mt-4" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
      <Row>
        {/* Left column: case study tabs */}
        <Col md={5} style={{ borderRight: '1px solid #dee2e6', paddingRight: '20px' }}>
          <h2>{caseStudy?.title}</h2>
          {caseStudy && (
            <Tab.Container defaultActiveKey="history">
              <Nav variant="tabs">
                <Nav.Item><Nav.Link eventKey="history">History & Physical</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="lab">Laboratory</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="vitals">Vital Signs</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="notes">Nurses' Notes</Nav.Link></Nav.Item>
              </Nav>
              <Tab.Content className="mt-3">
                <Tab.Pane eventKey="history">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.history_physical }} />
                </Tab.Pane>
                <Tab.Pane eventKey="lab">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.laboratory }} />
                </Tab.Pane>
                <Tab.Pane eventKey="vitals">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.vital_signs }} />
                </Tab.Pane>
                <Tab.Pane eventKey="notes">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.nurses_notes }} />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Col>

        {/* Right column: current question review */}
        <Col md={7}>
          <p className="text-muted">Question {currentIndex + 1} of {total}</p>
          <div className="mb-3">
            <p><strong>{current.question_text}</strong></p>
            {renderOptions()}
          </div>
          <div
            className="p-3 bg-light rounded shadow-sm"
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          >
            <h5>Explanation</h5>
            <div style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {current.explanation}
            </div>
          </div>
        </Col>
      </Row>

      <div className="mt-4 d-flex justify-content-between align-items-center">
        <div>
          <Button variant="outline-primary" onClick={handlePrev} disabled={currentIndex === 0} className="me-2">
            ← Previous
          </Button>
          <Button variant="primary" onClick={handleNext} disabled={currentIndex === total - 1}>
            Next →
          </Button>
        </div>
        <div>
          <Button variant="danger" onClick={() => navigate(-1)} className="me-2">
            ← Back to Report
          </Button>
          <Button variant="outline-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default CaseStudyReview;